import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { createCheckoutSession } from '../api';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import './PricingPage.css';

const PLANS = [
  {
    key: 'free',
    name: 'Free',
    price: '$0',
    period: 'forever',
    desc: 'Get started and test the platform at zero cost.',
    features: ['1 active deal at a time', 'Basic listing on halfprice.ca', 'Redemption code system', 'Email support'],
    missing: ['Analytics dashboard', 'Featured placement', 'Priority support', 'Unlimited deals'],
    cta: 'Start free',
    href: '/business/register',
    highlight: false,
    stripePlan: null,
  },
  {
    key: 'starter',
    name: 'Starter',
    price: '$49',
    period: '/month CAD',
    desc: 'Everything you need to drive consistent local traffic.',
    features: ['Up to 5 active deals', 'Analytics dashboard', 'Redemption tracking', 'Neighbourhood targeting', 'Email support', 'Loyalty stamp system'],
    missing: ['Featured deal placement', 'Priority support'],
    cta: 'Upgrade to Starter',
    highlight: true,
    badge: 'Most popular',
    stripePlan: 'starter',
  },
  {
    key: 'pro',
    name: 'Pro',
    price: '$99',
    period: '/month CAD',
    desc: 'Maximum visibility for high-volume restaurants.',
    features: [
      'Unlimited active deals',
      '⭐ Deal of the Day — set any deal as your homepage hero',
      'Priority email support (same-day response)',
      'Full analytics dashboard',
      'Neighbourhood targeting',
      'Loyalty stamp system',
      'Billing portal — manage or cancel anytime',
    ],
    missing: [],
    cta: 'Upgrade to Pro',
    highlight: false,
    stripePlan: 'pro',
  },
];

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [error, setError] = useState('');

  const cancelled = searchParams.get('upgrade') === 'cancelled';

  const handleUpgrade = async (plan) => {
    if (!user || user.type !== 'business') {
      navigate('/business/login');
      return;
    }
    setLoadingPlan(plan);
    setError('');
    try {
      const res = await createCheckoutSession(plan);
      if (res.url) window.location.href = res.url;
    } catch (err) {
      setError(err.message || 'Failed to start checkout. Please try again.');
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <div className="pricing-page">
      <SEO
        title="Restaurant Listing Plans & Pricing — halfprice.ca"
        description="List your Winnipeg restaurant on halfprice.ca. Free plan available. Starter at $49/month, Pro at $99/month. Zero commission on every deal — ever."
        url="/pricing"
      />
      <div className="pricing-hero">
        <div className="container text-center">
          <h1 className="font-display pricing-title">Simple, honest pricing</h1>
          <p className="pricing-sub">No commissions. No hidden fees. Flat monthly rate to reach Winnipeg diners.</p>
          {cancelled && (
            <div className="pricing-cancelled">
              No worries — your plan wasn't changed. Take your time and upgrade when ready.
            </div>
          )}
          {error && <div className="pricing-error">{error}</div>}
        </div>
      </div>

      <div className="container">
        <div className="pricing-grid">
          {PLANS.map(plan => (
            <div key={plan.key} className={`pricing-card ${plan.highlight ? 'highlight' : ''}`}>
              {plan.badge && <div className="pricing-badge">{plan.badge}</div>}
              <div className="pricing-card-header">
                <div className="plan-name">{plan.name}</div>
                <div className="plan-price">
                  <span className="plan-price-num">{plan.price}</span>
                  <span className="plan-price-period">{plan.period}</span>
                </div>
                <p className="plan-desc">{plan.desc}</p>
              </div>
              <div className="pricing-card-body">
                <ul className="plan-features">
                  {plan.features.map(f => (
                    <li key={f} className="feature-yes">
                      <span className="feature-icon">✓</span>{f}
                    </li>
                  ))}
                  {plan.missing.map(f => (
                    <li key={f} className="feature-no">
                      <span className="feature-icon">—</span>{f}
                    </li>
                  ))}
                </ul>
                {plan.stripePlan ? (
                  <button
                    className={`btn w-full ${plan.highlight ? 'btn-primary' : 'btn-outline'}`}
                    style={{ justifyContent: 'center', marginTop: 24, padding: '12px' }}
                    disabled={loadingPlan === plan.stripePlan}
                    onClick={() => handleUpgrade(plan.stripePlan)}
                  >
                    {loadingPlan === plan.stripePlan ? 'Redirecting to payment…' : plan.cta + ' →'}
                  </button>
                ) : (
                  <Link
                    to={plan.href}
                    className="btn btn-ghost w-full"
                    style={{ justifyContent: 'center', marginTop: 24, padding: '12px' }}
                  >
                    {plan.cta}
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Featured slot */}
        <div className="featured-slot-card">
          <div className="featured-slot-left">
            <div className="featured-slot-badge">Add-on — available to all plans</div>
            <h3 className="font-display featured-slot-title">Exclusive Featured Slot</h3>
            <p className="featured-slot-desc">
              Want to <strong>guarantee</strong> your restaurant is the homepage hero for an entire week?
              This is the exclusive paid version — no rotation, no competition. Your deal, front and centre, every day of that week.
            </p>
            <ul className="featured-slot-features">
              <li>✓ Exclusive homepage hero for a full 7-day week</li>
              <li>✓ "Sponsored" badge on your deal card</li>
              <li>✓ Priority placement over all Pro plan rotation</li>
              <li>✓ Available to any plan — Free, Starter, or Pro</li>
            </ul>
            <div className="featured-compare">
              <div className="fc-row">
                <span>Pro plan — Deal of the Day</span>
                <span className="fc-val">Rotating · shared with other Pro restaurants · free with Pro</span>
              </div>
              <div className="fc-row fc-highlight">
                <span>Exclusive Featured Slot</span>
                <span className="fc-val">Guaranteed 7 days · no rotation · $99–$199/week</span>
              </div>
            </div>
          </div>
          <div className="featured-slot-right">
            <div className="featured-slot-price">
              <div className="fsp-range">$99 – $199</div>
              <div className="fsp-period">per week CAD</div>
            </div>
            <a
              href="mailto:hello@halfprice.ca?subject=Featured Slot Booking&body=Hi, I'd like to book a featured slot for my restaurant on halfprice.ca. Please let me know available weeks and pricing."
              className="btn btn-primary btn-lg"
              style={{ display: 'block', textAlign: 'center', marginTop: 16 }}
            >
              Email to book →
            </a>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,.45)', marginTop: 8, textAlign: 'center' }}>
              hello@halfprice.ca · We respond within 24hrs
            </p>
          </div>
        </div>

        {/* Trust signals */}
        <div className="pricing-trust">
          <div className="trust-item">🔒 <span>Payments secured by Stripe</span></div>
          <div className="trust-item">🇨🇦 <span>Prices in Canadian dollars</span></div>
          <div className="trust-item">↩ <span>Cancel anytime — no contracts</span></div>
          <div className="trust-item">💬 <span>hello@halfprice.ca support</span></div>
        </div>

        {/* FAQ */}
        <div className="pricing-faq">
          <h2 className="font-display">Common questions</h2>
          <div className="faq-grid">
            {[
              { q: 'Do you take commission on redemptions?', a: 'Never. You pay a flat monthly fee and keep 100% of every sale. We never touch your revenue.' },
              { q: 'Can I cancel anytime?', a: 'Yes, cancel anytime through the billing portal on your dashboard. Your plan stays active until the end of your billing period. No refunds for the current month — you simply won\'t be charged again.' },
              { q: 'How quickly will my deal go live?', a: 'Instantly after you submit. No approval delay — your deal appears on halfprice.ca within minutes of posting.' },
              { q: 'How does billing work?', a: 'We charge monthly via credit card through Stripe. You can view invoices, update your card, or cancel anytime from the billing portal on your dashboard.' },
              { q: 'What payment methods do you accept?', a: 'Visa, Mastercard, American Express, and most major credit cards. Processed securely by Stripe — we never see your card details.' },
              { q: 'Can I upgrade or downgrade later?', a: 'Yes. Upgrade anytime from your dashboard or the billing portal. Downgrades take effect at the end of your current billing period.' },
              { q: 'What does "email support" mean on Starter?', a: 'Email support means you can reach us directly at hello@halfprice.ca for help with your account, deals, or technical issues. We respond within 1–2 business days. Starter plan customers get standard response times.' },
              { q: 'What is "priority support" on Pro?', a: 'Pro plan restaurants get faster response times — we prioritise Pro enquiries and aim to respond within a few hours during business hours. Same email address: hello@halfprice.ca, just faster attention.' },
              { q: 'How does featured deal placement work on Pro?', a: 'Pro subscribers are eligible for featured placement in the homepage "Deal of the Day" section and appear with a Featured badge in search results. Placement rotates among eligible Pro restaurants. It is included in your Pro subscription at no extra cost.' },
              { q: 'What is the featured slot add-on ($99–$199/week)?', a: 'This is a reserved, exclusive homepage hero slot available to any plan by contacting us directly. Unlike the rotating featured placement included with Pro, this guarantees your deal is the very first thing every visitor sees for the entire week you book. Pricing depends on the week and season. Email hello@halfprice.ca to inquire and book.' },
            ].map(({ q, a }) => (
              <div key={q} className="faq-item">
                <h4>{q}</h4>
                <p>{a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="pricing-bottom-cta">
          <h2 className="font-display">Ready to reach more Winnipeg diners?</h2>
          <p>Start free today. Upgrade when you're ready.</p>
          <Link to="/business/register" className="btn btn-primary btn-lg">List your restaurant free →</Link>
        </div>
      </div>
    </div>
  );
}
